import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { List, Loader2, RotateCw, Bot, Clock, Eye, EyeOff, Angry, Frown } from 'lucide-react';
import { whatsappAPI } from './whatsappAPI';
import { Chip, telefoneConversa } from './organizacao';
import { semMarcas } from './waFormat';
import { fmtProtocolo } from './PainelCliente';

/* Kanban dos atendimentos. Cada coluna é um estado real da conversa, então o quadro se move
 * sozinho (assumiu, cliente respondeu, finalizou…) e arrastar um card executa a ação correspondente. */

const COLUNAS = [
  { id: 'bot', titulo: 'Triagem', cor: '#8b5cf6', ajuda: 'Com o assistente' },
  { id: 'fila', titulo: 'Na fila', cor: '#f59e0b', ajuda: 'Esperando um atendente' },
  { id: 'em_atendimento', titulo: 'Em atendimento', cor: '#059669' },
  { id: 'proposta_enviada', titulo: 'Proposta enviada', cor: '#6366f1' },
  { id: 'aguardando_cliente', titulo: 'Aguardando cliente', cor: '#0ea5e9' },
  { id: 'aguardando_setor', titulo: 'Aguardando setor', cor: '#f97316' },
  { id: 'aguardando_pagamento', titulo: 'Aguardando pagamento', cor: '#eab308' },
  { id: 'resolvido', titulo: 'Resolvido', cor: '#14b8a6' },
  { id: 'finalizado', titulo: 'Finalizados', cor: '#64748b', ajuda: 'Nas últimas 24 horas' },
];
const SITUACAO_COLS = ['em_atendimento', 'proposta_enviada', 'aguardando_cliente', 'aguardando_setor', 'aguardando_pagamento', 'resolvido'];

export const colunaDe = (c) => {
  const st = c.atendimento_status || 'bot';
  if (st === 'bot' || st === 'fila') return st;
  if (st === 'em_atendimento') return SITUACAO_COLS.includes(c.situacao) ? c.situacao : 'em_atendimento';
  return 'finalizado';
};

const nome = (c) => c.cliente_nome || c.push_name || telefoneConversa(c) || 'Contato do WhatsApp';
const ha = (iso) => {
  if (!iso) return '';
  const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h${String(m % 60).padStart(2, '0')}` : `${Math.floor(h / 24)}d`;
};
const lerOcultas = () => { try { return JSON.parse(localStorage.getItem('wa_kanban_ocultas') || '[]'); } catch (_) { return []; } };

/* o que acontece ao soltar o card numa coluna; null = não permitido */
function acaoDoMovimento(de, para) {
  if (de === para) return null;
  if (para === 'bot') return null;
  if (de === 'finalizado') return para === 'em_atendimento' ? { tipo: 'reabrir' } : null;
  if (para === 'fila') return SITUACAO_COLS.includes(de) ? { tipo: 'fila' } : null;
  if (para === 'finalizado') return SITUACAO_COLS.includes(de) ? { tipo: 'finalizar' } : null;
  if (SITUACAO_COLS.includes(para)) return { tipo: 'situacao', assumir: de === 'fila' || de === 'bot', situacao: para };
  return null;
}

export default function KanbanAtendimento({ tick, onAbrir, onVoltar, userId, setores, etiquetas, setoresMap, etiquetasMap, Avatar }) {
  const [convs, setConvs] = useState(null);
  const [filtros, setFiltros] = useState({ setor: '', etiqueta: '', meus: false });
  const [arrastando, setArrastando] = useState(null);    // { jid, de }
  const [sobre, setSobre] = useState(null);
  const [ocultas, setOcultas] = useState(lerOcultas);
  const [verColunas, setVerColunas] = useState(false);

  const carregar = useCallback(async () => {
    try { const { data } = await whatsappAPI.kanban(filtros); setConvs(data || []); }
    catch (e) { setConvs(cs => cs || []); toast.error(e?.response?.data?.detail || 'Não foi possível carregar o quadro'); }
  }, [filtros]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { if (tick) { const t = setTimeout(carregar, 400); return () => clearTimeout(t); } return undefined; }, [tick, carregar]);
  useEffect(() => { const t = setInterval(() => { if (!document.hidden) carregar(); }, 60000); return () => clearInterval(t); }, [carregar]);

  const porColuna = useMemo(() => {
    const m = Object.fromEntries(COLUNAS.map(c => [c.id, []]));
    (convs || []).forEach(c => m[colunaDe(c)]?.push(c));
    for (const k of Object.keys(m)) m[k].sort((a, b) => String(b.ultima_data || '').localeCompare(String(a.ultima_data || '')));   // mais recente em cima
    return m;
  }, [convs]);

  const alternarColuna = (id) => setOcultas(o => {
    const n = o.includes(id) ? o.filter(x => x !== id) : [...o, id];
    try { localStorage.setItem('wa_kanban_ocultas', JSON.stringify(n)); } catch (_) { /* noop */ }
    return n;
  });

  const soltar = async (para) => {
    const a = arrastando; setArrastando(null); setSobre(null);
    if (!a) return;
    const acao = acaoDoMovimento(a.de, para);
    if (!acao) { toast.error('Esse movimento não é permitido aqui'); return; }
    if (acao.tipo === 'finalizar' && !window.confirm('Finalizar o atendimento?\nO cliente recebe a mensagem de encerramento.')) return;
    const antes = convs;
    // otimista: o card já aparece na coluna nova
    setConvs(cs => cs.map(c => (c.jid !== a.jid ? c : {
      ...c,
      ...(acao.tipo === 'situacao' ? { atendimento_status: 'em_atendimento', situacao: acao.situacao, ...(acao.assumir ? { atendente_id: userId } : {}) } : {}),
      ...(acao.tipo === 'fila' ? { atendimento_status: 'fila', atendente_id: null, fila_desde: new Date().toISOString() } : {}),
      ...(acao.tipo === 'finalizar' ? { atendimento_status: 'encerrada' } : {}),
      ...(acao.tipo === 'reabrir' ? { atendimento_status: 'em_atendimento', situacao: 'em_atendimento', atendente_id: userId } : {}),
    })));
    try {
      if (acao.tipo === 'situacao') {
        if (acao.assumir) await whatsappAPI.assumir(a.jid);
        if (acao.situacao !== 'em_atendimento' || !acao.assumir) await whatsappAPI.situacao(a.jid, acao.situacao);
      } else if (acao.tipo === 'fila') await whatsappAPI.devolverFila(a.jid);
      else if (acao.tipo === 'finalizar') await whatsappAPI.finalizar(a.jid);
      else if (acao.tipo === 'reabrir') await whatsappAPI.reabrir(a.jid);
    } catch (e) {
      setConvs(antes);
      toast.error(e?.response?.data?.detail || 'Não foi possível mover');
    }
    carregar();
  };

  const visiveis = COLUNAS.filter(c => !ocultas.includes(c.id));
  const selectCls = 'h-8 px-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm outline-none border-0';

  return (
    <div className="flex flex-col h-[calc(100dvh-10rem)] min-h-[520px] rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950" data-testid="wa-kanban">
      <div className="px-4 py-3 flex flex-wrap items-center gap-2 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <h1 className="text-lg font-bold mr-2">Quadro de atendimentos</h1>
        <div className="flex rounded-lg bg-slate-100 dark:bg-slate-800 p-0.5 text-sm font-medium">
          <button onClick={onVoltar} className="h-7 px-2.5 rounded-md flex items-center gap-1.5 text-slate-600 dark:text-slate-300" data-testid="wa-modo-lista">
            <List className="w-4 h-4" /> Lista
          </button>
          <span className="h-7 px-2.5 rounded-md flex items-center gap-1.5 bg-white dark:bg-slate-900 shadow-sm text-emerald-700 dark:text-emerald-400">Quadro</span>
        </div>
        <span className="flex-1" />
        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input type="checkbox" checked={filtros.meus} onChange={e => setFiltros(f => ({ ...f, meus: e.target.checked }))} className="w-4 h-4 accent-emerald-600" data-testid="wa-kanban-meus" />
          Só os meus
        </label>
        {setores.length > 0 && (
          <select value={filtros.setor} onChange={e => setFiltros(f => ({ ...f, setor: e.target.value }))} className={selectCls} data-testid="wa-kanban-setor">
            <option value="">Todos os setores</option>
            {setores.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            <option value="sem">Sem setor</option>
          </select>
        )}
        {etiquetas.length > 0 && (
          <select value={filtros.etiqueta} onChange={e => setFiltros(f => ({ ...f, etiqueta: e.target.value }))} className={selectCls}>
            <option value="">Todas as etiquetas</option>
            {etiquetas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
          </select>
        )}
        <div className="relative">
          <button onClick={() => setVerColunas(v => !v)} className="h-8 px-2.5 rounded-lg border border-slate-200 dark:border-slate-700 text-sm flex items-center gap-1.5 hover:bg-slate-50 dark:hover:bg-slate-800">
            <Eye className="w-4 h-4" /> Colunas{ocultas.length ? ` (${COLUNAS.length - ocultas.length}/${COLUNAS.length})` : ''}
          </button>
          {verColunas && (
            <div className="absolute right-0 top-10 z-30 w-56 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg py-1" onMouseLeave={() => setVerColunas(false)}>
              {COLUNAS.map(c => (
                <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer">
                  <input type="checkbox" checked={!ocultas.includes(c.id)} onChange={() => alternarColuna(c.id)} className="w-4 h-4 accent-emerald-600" />
                  <span className="w-2 h-2 rounded-full" style={{ background: c.cor }} />{c.titulo}
                </label>
              ))}
            </div>
          )}
        </div>
        <button onClick={carregar} title="Atualizar" className="h-8 w-8 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center"><RotateCw className="w-4 h-4" /></button>
      </div>

      {!convs ? (
        <div className="flex-1 flex items-center justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="flex-1 min-h-0 overflow-x-auto">
          <div className="h-full flex gap-3 p-3 w-max">
            {visiveis.map(col => {
              const itens = porColuna[col.id] || [];
              const permitido = arrastando ? !!acaoDoMovimento(arrastando.de, col.id) || arrastando.de === col.id : true;
              return (
                <section key={col.id} data-testid={`wa-kanban-col-${col.id}`}
                  onDragOver={e => { if (arrastando && acaoDoMovimento(arrastando.de, col.id)) { e.preventDefault(); setSobre(col.id); } }}
                  onDragLeave={() => setSobre(s => (s === col.id ? null : s))}
                  onDrop={e => { e.preventDefault(); soltar(col.id); }}
                  className={`w-[272px] shrink-0 flex flex-col min-h-0 rounded-xl transition-colors ${sobre === col.id ? 'bg-emerald-50 dark:bg-emerald-950/40 ring-2 ring-emerald-400/60' : 'bg-slate-100/80 dark:bg-slate-900/70'} ${arrastando && !permitido ? 'opacity-40' : ''}`}>
                  <header className="px-3 pt-3 pb-2">
                    <div className="h-1 rounded-full mb-2.5" style={{ background: col.cor }} />
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-semibold">{col.titulo}</h2>
                      <span className="text-xs text-slate-500 tabular-nums">{itens.length}</span>
                      {col.id !== 'bot' && col.id !== 'fila' && col.id !== 'finalizado' ? null : (
                        <span className="text-[11px] text-slate-400 ml-auto truncate">{col.ajuda}</span>
                      )}
                    </div>
                  </header>
                  <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-2 space-y-2">
                    {itens.map(c => (
                      <Card key={c.jid} c={c} userId={userId} setor={setoresMap[c.setor_id]} etiquetasMap={etiquetasMap} Avatar={Avatar}
                        arrastando={arrastando?.jid === c.jid} onAbrir={() => onAbrir(c.jid)}
                        onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', c.jid); setArrastando({ jid: c.jid, de: col.id }); }}
                        onDragEnd={() => { setArrastando(null); setSobre(null); }} />
                    ))}
                    {!itens.length && (
                      <p className="text-xs text-slate-400 text-center py-6">
                        {arrastando && acaoDoMovimento(arrastando.de, col.id) ? 'Solte aqui' : 'Nenhuma conversa'}
                      </p>
                    )}
                  </div>
                </section>
              );
            })}
            {ocultas.length > 0 && (
              <button onClick={() => setVerColunas(true)} className="w-10 shrink-0 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 text-slate-400 flex items-center justify-center" title="Colunas ocultas">
                <EyeOff className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ c, userId, setor, etiquetasMap, Avatar, arrastando, onAbrir, onDragStart, onDragEnd }) {
  const ets = (c.etiquetas || []).map(id => etiquetasMap[id]).filter(Boolean);
  const meu = c.atendente_id && c.atendente_id === userId;
  const tempo = c.atendimento_status === 'fila' ? c.fila_desde : c.ultima_data;
  return (
    <article draggable onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={onAbrir} data-testid={`wa-kanban-card-${c.numero || c.jid}`}
      role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onAbrir()}
      className={`group rounded-lg bg-white dark:bg-slate-800 p-2.5 cursor-grab active:cursor-grabbing border border-transparent hover:border-slate-300 dark:hover:border-slate-600 shadow-[0_1px_2px_rgba(15,23,42,.08)] ${arrastando ? 'opacity-40' : ''}`}
      style={{ ...(setor ? { borderLeft: `3px solid ${setor.cor}` } : {}) }}>
      <div className="flex items-start gap-2">
        <Avatar name={nome(c)} size={30} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            <p className="text-[13px] font-semibold truncate flex-1">{nome(c)}</p>
            {c.ia_analise?.sentimento === 'irritado' && <Angry className="w-3.5 h-3.5 text-red-600 shrink-0" aria-label="Cliente irritado (IA)" />}
            {c.ia_analise?.sentimento === 'negativo' && <Frown className="w-3.5 h-3.5 text-amber-600 shrink-0" aria-label="Cliente insatisfeito (IA)" />}
            {c.nao_lidas > 0 && <span className="bg-emerald-500 text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">{c.nao_lidas}</span>}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{semMarcas(c.ultima_mensagem || '')}</p>
        </div>
      </div>
      {(setor || ets.length > 0) && (
        <div className="flex flex-wrap gap-1 mt-2">
          {setor && <Chip cor={setor.cor} small>{setor.nome}</Chip>}
          {ets.slice(0, 3).map(e => <Chip key={e.id} cor={e.cor} small>{e.nome}</Chip>)}
          {ets.length > 3 && <span className="text-[10.5px] text-slate-500">+{ets.length - 3}</span>}
        </div>
      )}
      <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-500 dark:text-slate-400">
        {c.atendimento_status === 'bot' ? <Bot className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
        <span>{ha(tempo)}</span>
        {c.protocolo && <span className="truncate">{fmtProtocolo(c.protocolo)}</span>}
        <span className="flex-1" />
        {c.atendente_nome && c.atendimento_status === 'em_atendimento' && (
          <span className={`shrink-0 rounded-full px-1.5 py-px font-medium ${meu ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200'}`}>
            {meu ? 'você' : c.atendente_nome.split(' ')[0]}
          </span>
        )}
      </div>
    </article>
  );
}
